import { useMemo, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  DatePicker,
  GenderRadioGroup,
  InputLabel,
  Select,
  TextInput,
  TimeRangeSlider,
} from "components";
import { convertToTimeString } from "components/time";
import { getRequiredFieldStyles } from "pages/clients/view/components/required-field-styles";
import SectionTitle, { SectionTitleIndicatorType } from "pages/clients/view/components/section-title";

import { CreditOptionsResponse, CreditRequestForm } from "types";
import { AppUtils } from "utils";
import { useCreditRequestMode } from "../request-mode-context";

interface CreditRequestPersonalInfoProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  updateCredit: (item: Partial<CreditRequestForm>) => Promise<void>;
  hasWarning?: boolean;
  onWarningClick?: () => void;
  indicatorType?: SectionTitleIndicatorType;
}

const CreditRequestPersonalInfo = ({
  form,
  options,
  visibleFields,
  readOnly = false,
  updateCredit,
  hasWarning = false,
  onWarningClick,
  indicatorType = "warning",
}: CreditRequestPersonalInfoProps) => {
  const { t } = useTranslation();
  const { isPreliminary } = useCreditRequestMode();
  const availabilitySliderMaxWidth = 420;
  const [visaInputKey, setVisaInputKey] = useState(0);

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options["nationality"], t),
    [options, t],
  );

  const getRequiredSelectStyles = (fieldName: "nationality" | "visaType") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = typeof value !== "string" || value.trim() === "";

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  const getRequiredDatePickerStyles = (fieldName: "birthDate" | "visaFrom") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = !value;
    const isVisible = fieldName === "birthDate" || visibleFields.includes(fieldName);

    return getRequiredFieldStyles({
      readOnly,
      isVisible,
      isEmpty,
    });
  };

  return (
    <Box>
      <SectionTitle
        label={t("credit.personalInfo.sectionTitle")}
        hasWarning={hasWarning}
        onWarningClick={onWarningClick}
        indicatorType={indicatorType}
      />
      <GenderRadioGroup
        fieldName="gender"
        form={form}
        disabled={readOnly}
        required={!isPreliminary}
      />

      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.firstName.label")}
          key={form.key("user.firstName")}
          {...form.getInputProps("user.firstName")}
          disabled={true}
          required={!isPreliminary}
        />
        <TextInput
          label={t("user.lastName.label")}
          key={form.key("user.lastName")}
          {...form.getInputProps("user.lastName")}
          disabled={true}
          required={!isPreliminary}
        />
        <DatePicker
          label={t("user.birthDate.label")}
          placeholder={t("user.birthDate.placeholder")}
          key={form.key("birthDate")}
          {...form.getInputProps("birthDate")}
          styles={isPreliminary ? undefined : getRequiredDatePickerStyles("birthDate")}
          minDate={moment().subtract(65, "years")}
          maxDate={moment().subtract(18, "years")}
          required={!isPreliminary}
          disabled={readOnly}
        />
      </SimpleGrid>
      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.phone.label")}
          key={form.key("user.phone")}
          {...form.getInputProps("user.phone")}
          disabled={true}
          required={!isPreliminary}
        />
        <TextInput
          label={t("user.email.label")}
          key={form.key("user.email")}
          {...form.getInputProps("user.email")}
          disabled={true}
          required={!isPreliminary}
        />
      </SimpleGrid>

      <Box
        mt={"sm"}
        style={{
          alignItems: "flex-start",
          columnGap: "24px",
          display: "flex",
          flexWrap: "wrap",
          rowGap: "12px",
        }}
      >
        <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
          <InputLabel label={t("credit.workdayAvailability.label")} />
          <TimeRangeSlider
            min={options["workdayAvailability"]?.min ?? 0}
            max={options["workdayAvailability"]?.max ?? 23}
            maxWidth={availabilitySliderMaxWidth}
            valueFrom={form.getValues().workdayAvailabilityFrom}
            valueTo={form.getValues().workdayAvailabilityTo}
            disabled={readOnly}
            onChangeEnd={(valueFrom, valueTo) => {
              form.setValues({
                workdayAvailabilityFrom: valueFrom,
                workdayAvailabilityTo: valueTo,
              });
              updateCredit({
                workdayAvailabilityFrom: valueFrom,
                workdayAvailabilityTo: valueTo,
              });
            }}
          />
          <Checkbox
            key={form.key("saturdayAvailability")}
            label={t("credit.saturdayAvailability.label")}
            {...form.getInputProps("saturdayAvailability", { type: "checkbox" })}
            customOnClick={() => {
              form.setFieldValue(
                "saturdayAvailabilityFrom",
                convertToTimeString(options["saturdayAvailability"]?.min || 0),
              );
              form.setFieldValue(
                "saturdayAvailabilityTo",
                convertToTimeString(options["saturdayAvailability"]?.max || 0),
              );
            }}
            disabled={readOnly}
            mt={"sm"}
          />
        </Box>

        {visibleFields.includes("saturdayAvailabilityFrom") &&
          visibleFields.includes("saturdayAvailabilityTo") && (
            <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
              <InputLabel label={t("credit.saturdayAvailability.label")} />
              <TimeRangeSlider
                min={options["saturdayAvailability"]?.min ?? 0}
                max={options["saturdayAvailability"]?.max ?? 23}
                maxWidth={availabilitySliderMaxWidth}
                valueFrom={form.getValues().saturdayAvailabilityFrom}
                valueTo={form.getValues().saturdayAvailabilityTo}
                disabled={readOnly}
                onChangeEnd={(valueFrom, valueTo) => {
                  form.setValues({
                    saturdayAvailabilityFrom: valueFrom,
                    saturdayAvailabilityTo: valueTo,
                  });
                  updateCredit({
                    saturdayAvailabilityFrom: valueFrom,
                    saturdayAvailabilityTo: valueTo,
                  });
                }}
              />
            </Box>
          )}
      </Box>

      <SimpleGrid cols={4} mt="sm">
        <Select
          label={t("user.nationality.label")}
          data={nationalities}
          key={form.key("nationality")}
          {...form.getInputProps("nationality")}
          styles={isPreliminary ? undefined : getRequiredSelectStyles("nationality")}
          searchable
          clearable={true}
          required={!isPreliminary}
          disabled={readOnly}
        />
        {visibleFields.includes("visaType") && (
          <Select
            label={t("user.visa.label")}
            data={options["visaType"].map((el) => ({
              value: el,
              label: t(`user.visa.options.${el}`),
            }))}
            key={form.key("visaType")}
            {...form.getInputProps("visaType")}
            styles={isPreliminary ? undefined : getRequiredSelectStyles("visaType")}
            clearable={true}
            required={!isPreliminary}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("visaFrom") && (
          <DatePicker
            label={t("user.visa.from")}
            key={`${form.key("visaFrom")}-${visaInputKey}`}
            {...form.getInputProps("visaFrom")}
            styles={isPreliminary ? undefined : getRequiredDatePickerStyles("visaFrom")}
            excludeDate={(date: Date) => {
              const birthDate = form.getValues().birthDate;

              return Boolean(birthDate && moment(date).isBefore(birthDate, "day"));
            }}
            maxDate={new Date()}
            onChange={(value: Date | null) => {
              const birthDate = form.getValues().birthDate;

              if (value && birthDate && moment(value).isBefore(birthDate, "day")) {
                form.setFieldError("visaFrom", t("user.visa.validation.beforeBirthDate"));
                setVisaInputKey((key) => key + 1);
                return;
              }

              form.setFieldValue("visaFrom", value ?? undefined);
            }}
            required={!isPreliminary}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>
    </Box>
  );
};

export default CreditRequestPersonalInfo;
